import { fullscreenSectionHeightMinusMargins } from '@features/cms/constants/spacingBlockHeightMap';
import theme from '@styles/theme';
import { FLUID_SECTION_HORIZONTAL_PADDING_CSS, getFluidSectionPaddingCss } from '@utils/fluidSectionHorizontalPadding';
import { ThemeUIStyleObject } from 'theme-ui';
type ButtonVariant = 'outline' | 'filled';
type StylesType = {
  wrapper: (backgroundColor?: string, fullWidth?: boolean) => ThemeUIStyleObject;
  section: (marginTopPx?: number, marginBottomPx?: number) => ThemeUIStyleObject;
  sectionHeader: ThemeUIStyleObject;
  sectionHeaderTop: ThemeUIStyleObject;
  mediaWrapper: ThemeUIStyleObject;
  video: ThemeUIStyleObject;
  overlay: ThemeUIStyleObject;
  content: (variant: 'centered' | 'left' | 'bottom') => ThemeUIStyleObject;
  eyebrow: ThemeUIStyleObject;
  textWrapper: (variant: 'centered' | 'left' | 'bottom', fullWidth?: boolean) => ThemeUIStyleObject;
  brandLogo: ThemeUIStyleObject;
  title: (withEyebrow?: boolean) => ThemeUIStyleObject;
  subtitle: ThemeUIStyleObject;
  ctaButton: (variant?: ButtonVariant, color?: string) => ThemeUIStyleObject;
  productsButtonWrapper: ThemeUIStyleObject;
};
const styles: StylesType = {
  sectionHeaderTop: {
    position: 'absolute',
    marginBottom: 0,
    padding: getFluidSectionPaddingCss([24, 24, 64, 80, 80, 80]),
    color: theme.rawColors.neutralWhite,
    zIndex: theme.zIndex.documentUp
  },
  sectionHeader: {
    marginBottom: 0,
    padding: FLUID_SECTION_HORIZONTAL_PADDING_CSS
  },
  wrapper: (backgroundColor?: string, fullWidth?: boolean) => ({
    width: '100%',
    backgroundColor: backgroundColor || theme.rawColors.neutralWhite,
    padding: fullWidth ? 0 : FLUID_SECTION_HORIZONTAL_PADDING_CSS,
    paddingTop: 0,
    paddingBottom: 0
  }),
  section: (marginTopPx?: number, marginBottomPx?: number) => ({
    position: 'relative',
    width: '100%',
    height: fullscreenSectionHeightMinusMargins(marginTopPx, marginBottomPx),
    overflow: 'hidden'
  }),
  mediaWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 0
  },
  video: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    zIndex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  content: variant => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: variant === 'centered' || variant === 'bottom' ? 'center' : 'flex-start',
    justifyContent: variant === 'bottom' ? 'flex-end' : 'center',
    padding: variant === 'left' ? ['64px', '64px', '48px 48px 48px 100px', '64px 64px 64px 150px', '80px 80px 80px 200px', '100px 100px 100px 229px'] : ['64px', '64px', '48px', '64px', '80px', '100px']
  }),
  eyebrow: {
    position: 'absolute',
    top: ['16px', '24px', '40px', '53px', '60px', '80px'],
    left: ['16px', '24px', '40px', '56px', '56px', '80px'],
    fontFamily: theme.fonts.uniformExtra,
    fontWeight: theme.fontWeights.normal,
    fontSize: ['32px', '32px', '32px', '32px', '40px'],
    letterSpacing: '5%',
    lineHeight: '110%',
    textTransform: 'uppercase',
    color: theme.rawColors.neutralWhite,
    textShadow: '0px 0px 100px #000',
    zIndex: 2
  },
  textWrapper: (variant, fullWidth?: boolean) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: variant === 'centered' || variant === 'bottom' ? 'center' : 'flex-start',
    textAlign: variant === 'centered' || variant === 'bottom' ? 'center' : 'left',
    justifyContent: variant === 'bottom' ? 'flex-end' : 'flex-start',
    ...(variant === 'left' && !fullWidth ? {
      maxWidth: '400px'
    } : {})
  }),
  brandLogo: {
    marginBottom: ['12px', '16px', '20px', '24px'],
    '& img': {
      maxHeight: ['24px', '28px', '32px', '40px'],
      width: 'auto'
    }
  },
  title: (withEyebrow?: boolean) => ({
    fontFamily: theme.fonts.uniformMedium,
    fontWeight: theme.fontWeights.medium,
    color: theme.rawColors.neutralWhite,
    fontSize: withEyebrow ? 'clamp(56px, calc(29.2px + 7.14vw), 132px)' : 'clamp(42px, calc(20.2px + 5.82vw), 104px)',
    lineHeight: '96%',
    margin: 0,
    whiteSpace: ['normal', 'normal', 'nowrap', 'nowrap', 'nowrap', 'nowrap']
  }),
  subtitle: {
    fontFamily: theme.fonts.uniformMedium,
    fontWeight: theme.fontWeights.medium,
    color: theme.rawColors.neutralWhite,
    fontSize: ['16px', '16px', '16px', '18px', '24px'],
    fontStyle: 'normal',
    lineHeight: ['120%', '170%', '170%', '170%', '170%', '170%'],
    margin: 0,
    marginBottom: ['24px', '24px', '24px', '24px', '32px', '40px'],
    maxWidth: '1024px',
    textShadow: '0px 0px 100px #000'
  },
  ctaButton: (variant = 'outline', color) => {
    const buttonColor = color || theme.rawColors.accent700;
    const isOutline = variant === 'outline';
    return {
      display: 'inline-block',
      fontFamily: theme.fonts.uniform,
      fontWeight: theme.fontWeights.normal,
      fontSize: ['14px', '14px', '14px', '16px', '16px', '16px'],
      textTransform: 'lowercase',
      letterSpacing: '1%',
      padding: '10px 24px 9px',
      backgroundColor: isOutline ? theme.rawColors.neutralWhite : buttonColor,
      color: isOutline ? theme.rawColors.neutralBlack : theme.rawColors.neutralWhite,
      border: 'none',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      '&:hover': {
        backgroundColor: isOutline ? theme.rawColors.neutralBlack : theme.rawColors.darkGrey700,
        color: theme.rawColors.neutralWhite
      }
    };
  },
  productsButtonWrapper: {
    position: 'absolute',
    bottom: ['20px', '24px', '32px', '40px'],
    left: ['20px', '24px', '32px', '40px']
  }
};
export default styles;